import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { FileText, Plus, Eye, Clock, CheckCircle, Search, Pencil, Trash2, X } from "lucide-react";
import type { Client, InvoiceWithDetails } from "@shared/schema";

type SchedulerStatus = {
  running: boolean;
  lastRunTime: string | null;
  lastRunResults: { clientName: string; invoiceId: string; totalAmount: string }[];
  currentStockholmTime: string;
  currentWeekday: number;
  scheduled: { clientName: string; cadence: string; weekday: number; time: string }[];
};

const weekdayNames: Record<number, string> = {
  0: "Söndag",
  1: "Måndag",
  2: "Tisdag",
  3: "Onsdag",
  4: "Torsdag",
  5: "Fredag",
  6: "Lördag",
};

const getUnitLabel = (unit: string) => {
  switch (unit) {
    case "pcs": return "st";
    case "bag": return "påse";
    case "box": return "låda";
    default: return unit;
  }
};

export default function AdminInvoicing() {
  const { toast } = useToast();
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [viewInvoice, setViewInvoice] = useState<InvoiceWithDetails | null>(null);
  const [editInvoice, setEditInvoice] = useState<InvoiceWithDetails | null>(null);
  const [deletingInvoiceId, setDeletingInvoiceId] = useState<string | null>(null);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterClient, setFilterClient] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const [editPeriodStart, setEditPeriodStart] = useState("");
  const [editPeriodEnd, setEditPeriodEnd] = useState("");
  const [editStatus, setEditStatus] = useState("");

  const { data: invoices, isLoading } = useQuery<InvoiceWithDetails[]>({
    queryKey: ["/api/admin/invoices"],
  });

  const { data: clients } = useQuery<Client[]>({
    queryKey: ["/api/admin/clients"],
  });

  const { data: schedulerStatus } = useQuery<SchedulerStatus>({
    queryKey: ["/api/admin/invoicing/scheduler-status"],
    refetchInterval: 30000,
  });

  const generateMutation = useMutation({
    mutationFn: (data: { clientId: string; periodStart: string; periodEnd: string }) =>
      apiRequest("POST", "/api/admin/invoices/generate", data),
    onSuccess: () => {
      toast({ title: "Faktura genererad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/invoices"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/invoiced-order-ids"] });
      setGenerateDialogOpen(false);
      setSelectedClientId("");
      setStartDate("");
      setEndDate("");
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte generera faktura.", variant: "destructive" });
    },
  });

  const markPaidMutation = useMutation({
    mutationFn: (invoiceId: string) =>
      apiRequest("PATCH", `/api/admin/invoices/${invoiceId}/status`, { status: "paid" }),
    onSuccess: () => {
      toast({ title: "Faktura markerad som betald!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/invoices"] });
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte uppdatera faktura.", variant: "destructive" });
    },
  });

  const updateInvoiceMutation = useMutation({
    mutationFn: (data: { id: string; periodStart?: string; periodEnd?: string; status?: string }) =>
      apiRequest("PATCH", `/api/admin/invoices/${data.id}`, data),
    onSuccess: () => {
      toast({ title: "Faktura uppdaterad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/invoices"] });
      setEditInvoice(null);
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte uppdatera faktura.", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (invoiceId: string) =>
      apiRequest("DELETE", `/api/admin/invoices/${invoiceId}`),
    onSuccess: () => {
      toast({ title: "Faktura raderad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/invoices"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/invoiced-order-ids"] });
      setDeletingInvoiceId(null);
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte radera faktura.", variant: "destructive" });
    },
  });

  const deleteItemMutation = useMutation({
    mutationFn: async (data: { invoiceId: string; itemId: string }) => {
      const result = await apiRequest("DELETE", `/api/admin/invoices/${data.invoiceId}/items/${data.itemId}`);
      return result;
    },
    onSuccess: (_data: any) => {
      toast({ title: "Order borttagen från faktura!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/invoices"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/invoiced-order-ids"] });
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte ta bort order från faktura.", variant: "destructive" });
    },
  });

  const openEdit = (invoice: InvoiceWithDetails) => {
    setEditInvoice(invoice);
    setEditPeriodStart(invoice.periodStart ? new Date(invoice.periodStart).toISOString().split("T")[0] : "");
    setEditPeriodEnd(invoice.periodEnd ? new Date(invoice.periodEnd).toISOString().split("T")[0] : "");
    setEditStatus(invoice.status);
  };

  const handleSaveEdit = () => {
    if (!editInvoice) return;
    updateInvoiceMutation.mutate({
      id: editInvoice.id,
      periodStart: editPeriodStart,
      periodEnd: editPeriodEnd,
      status: editStatus,
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "draft":
        return <Badge variant="secondary">Utkast</Badge>;
      case "sent":
        return <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/20">Skickad</Badge>;
      case "paid":
        return <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">Betald</Badge>;
      case "overdue":
        return <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/20">Förfallen</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Fakturering</h1>
        <p className="text-muted-foreground">Generera och hantera kundfakturor</p>
      </div>

      {schedulerStatus && (
        <Card data-testid="card-auto-invoicing">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-muted-foreground" />
              <CardTitle>Automatisk fakturering</CardTitle>
              {schedulerStatus.running ? (
                <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Aktiv
                </Badge>
              ) : (
                <Badge variant="secondary">Inaktiv</Badge>
              )}
            </div>
            <CardDescription>
              Veckovis: genereras på vald dag med de senaste 7 dagarnas ordrar. Månadsvis: genereras sista dagen i månaden med hela månadens ordrar.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {schedulerStatus.scheduled.length > 0 ? (
              <div className="space-y-3">
                <p className="text-sm font-medium">Schemalagda kunder</p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Kund</TableHead>
                      <TableHead>Period</TableHead>
                      <TableHead>Dag</TableHead>
                      <TableHead>Tid</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {schedulerStatus.scheduled.map((item, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{item.clientName}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {item.cadence === "weekly" ? "Veckovis" : "Månadsvis"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {item.cadence === "monthly" ? "Sista dagen i månaden" : (weekdayNames[item.weekday] || item.weekday)}
                        </TableCell>
                        <TableCell>{item.time}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {schedulerStatus.lastRunTime && (
                  <p className="text-xs text-muted-foreground">
                    Senaste kontroll: {new Date(schedulerStatus.lastRunTime).toLocaleString("sv-SE")}
                  </p>
                )}
                {schedulerStatus.lastRunResults.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Senast genererade</p>
                    {schedulerStatus.lastRunResults.map((r, i) => (
                      <p key={i} className="text-sm text-muted-foreground">
                        {r.clientName}: {r.totalAmount} kr
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Ingen kund har konfigurerad automatisk fakturering. Ställ in period, dag och tid under Kunder.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div>
            <CardTitle>Fakturor</CardTitle>
            <CardDescription>Alla genererade fakturor</CardDescription>
          </div>
          <Button onClick={() => setGenerateDialogOpen(true)} data-testid="button-generate-invoice">
            <Plus className="h-4 w-4 mr-2" />
            Generera faktura
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Sök fakturanr, kund..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="input-invoice-search"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[160px]" data-testid="select-filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alla statusar</SelectItem>
                <SelectItem value="draft">Utkast</SelectItem>
                <SelectItem value="sent">Skickad</SelectItem>
                <SelectItem value="paid">Betald</SelectItem>
                <SelectItem value="overdue">Förfallen</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterClient} onValueChange={setFilterClient}>
              <SelectTrigger className="w-[180px]" data-testid="select-filter-client">
                <SelectValue placeholder="Kund" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alla kunder</SelectItem>
                {clients?.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (() => {
            const filtered = (invoices || []).filter((inv) => {
              if (filterStatus !== "all" && inv.status !== filterStatus) return false;
              if (filterClient !== "all" && inv.clientId !== filterClient) return false;
              if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase();
                const clientName = inv.client?.name?.toLowerCase() || "";
                const invoiceNum = String(inv.invoiceNumber || "");
                if (!clientName.includes(q) && !invoiceNum.includes(q)) return false;
              }
              return true;
            });
            return filtered.length > 0 ? (
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fakturanr</TableHead>
                  <TableHead>Kund</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Belopp</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Åtgärd</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell className="font-medium">#{invoice.invoiceNumber}</TableCell>
                    <TableCell>{invoice.client?.name || "-"}</TableCell>
                    <TableCell>
                      {invoice.periodStart ? new Date(invoice.periodStart).toLocaleDateString("sv-SE") : "-"}
                      {" - "}
                      {invoice.periodEnd ? new Date(invoice.periodEnd).toLocaleDateString("sv-SE") : "-"}
                    </TableCell>
                    <TableCell>{invoice.totalAmount} kr</TableCell>
                    <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => setViewInvoice(invoice)} data-testid={`button-view-invoice-${invoice.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => openEdit(invoice)} data-testid={`button-edit-invoice-${invoice.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {invoice.status !== "paid" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => markPaidMutation.mutate(invoice.id)}
                            disabled={markPaidMutation.isPending}
                            data-testid={`button-mark-paid-${invoice.id}`}
                          >
                            Betald
                          </Button>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => setDeletingInvoiceId(invoice.id)}
                          data-testid={`button-delete-invoice-${invoice.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          ) : (
            <div className="py-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {invoices && invoices.length > 0 ? "Inga fakturor matchar filtren" : "Inga fakturor genererade"}
              </p>
            </div>
          );
          })()}
        </CardContent>
      </Card>

      <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Generera faktura</DialogTitle>
            <DialogDescription>Välj kund och period för att generera en faktura</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Kund</label>
              <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                <SelectTrigger data-testid="select-invoice-client">
                  <SelectValue placeholder="Välj kund" />
                </SelectTrigger>
                <SelectContent>
                  {clients?.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Från</label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="input-invoice-start-date"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Till</label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="input-invoice-end-date"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateDialogOpen(false)}>
              Avbryt
            </Button>
            <Button
              onClick={() => generateMutation.mutate({ clientId: selectedClientId, periodStart: startDate, periodEnd: endDate })}
              disabled={generateMutation.isPending || !selectedClientId || !startDate || !endDate}
              data-testid="button-confirm-generate"
            >
              {generateMutation.isPending ? "Genererar..." : "Generera"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewInvoice} onOpenChange={(open) => !open && setViewInvoice(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Fakturadetaljer</DialogTitle>
            <DialogDescription>
              Faktura #{viewInvoice?.invoiceNumber}
            </DialogDescription>
          </DialogHeader>
          {viewInvoice && (() => {
            const productTotals: Record<string, { name: string; unit: string; quantity: number; unitPrice: string; totalPrice: number }> = {};
            viewInvoice.items?.forEach((item) => {
              item.order?.items?.forEach((orderItem) => {
                const key = orderItem.productId;
                if (!productTotals[key]) {
                  productTotals[key] = {
                    name: orderItem.product?.name || "Okänd produkt",
                    unit: orderItem.product?.sellUnit || "pcs",
                    quantity: 0,
                    unitPrice: orderItem.unitPrice || "0",
                    totalPrice: 0,
                  };
                }
                productTotals[key].quantity += orderItem.quantity;
                productTotals[key].totalPrice += parseFloat(orderItem.totalPrice || "0");
              });
            });
            const aggregatedProducts = Object.values(productTotals).sort((a, b) => a.name.localeCompare(b.name, "sv"));

            return (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-muted-foreground">Kund:</span>
                  <span>{viewInvoice.client?.name}</span>
                  <span className="text-muted-foreground">Period:</span>
                  <span>
                    {viewInvoice.periodStart ? new Date(viewInvoice.periodStart).toLocaleDateString("sv-SE") : "-"}
                    {" - "}
                    {viewInvoice.periodEnd ? new Date(viewInvoice.periodEnd).toLocaleDateString("sv-SE") : "-"}
                  </span>
                  <span className="text-muted-foreground">Status:</span>
                  <span>{getStatusBadge(viewInvoice.status)}</span>
                  <span className="text-muted-foreground">Ordrar:</span>
                  <span>{viewInvoice.items?.length || 0} st</span>
                  <span className="text-muted-foreground">Totalt:</span>
                  <span className="font-medium">{viewInvoice.totalAmount} kr</span>
                </div>

                {aggregatedProducts.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-sm font-medium">Produktsammanställning</p>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Produkt</TableHead>
                          <TableHead className="text-right">Totalt antal</TableHead>
                          <TableHead className="text-right">Styckpris</TableHead>
                          <TableHead className="text-right">Summa</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {aggregatedProducts.map((prod, i) => (
                          <TableRow key={i} data-testid={`row-invoice-product-total-${i}`}>
                            <TableCell className="text-sm">{prod.name}</TableCell>
                            <TableCell className="text-sm text-right">
                              {prod.quantity} {getUnitLabel(prod.unit)}
                            </TableCell>
                            <TableCell className="text-sm text-right">{prod.unitPrice} kr</TableCell>
                            <TableCell className="text-sm text-right">{prod.totalPrice.toFixed(2)} kr</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    <div className="border-t pt-3 flex justify-between items-center">
                      <span className="font-medium">Totalt</span>
                      <span className="font-bold text-lg">{viewInvoice.totalAmount} kr</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editInvoice} onOpenChange={(open) => !open && setEditInvoice(null)}>
        <DialogContent className="max-w-2xl p-0">
          <DialogHeader className="px-4 pt-4 pb-2">
            <DialogTitle>Redigera faktura</DialogTitle>
            <DialogDescription>
              Faktura #{editInvoice?.invoiceNumber} - {editInvoice?.client?.name}
            </DialogDescription>
          </DialogHeader>
          {editInvoice && (
            <>
              <div className="max-h-[60vh] overflow-y-auto px-4">
                <div className="space-y-4 pb-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Period från</label>
                      <Input
                        type="date"
                        value={editPeriodStart}
                        onChange={(e) => setEditPeriodStart(e.target.value)}
                        data-testid="input-edit-period-start"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Period till</label>
                      <Input
                        type="date"
                        value={editPeriodEnd}
                        onChange={(e) => setEditPeriodEnd(e.target.value)}
                        data-testid="input-edit-period-end"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium">Status</label>
                    <Select value={editStatus} onValueChange={setEditStatus}>
                      <SelectTrigger data-testid="select-edit-status">
                        <SelectValue placeholder="Välj status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Utkast</SelectItem>
                        <SelectItem value="sent">Skickad</SelectItem>
                        <SelectItem value="paid">Betald</SelectItem>
                        <SelectItem value="overdue">Förfallen</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {editInvoice.items && editInvoice.items.length > 0 && (
                    <div className="space-y-3">
                      <p className="text-sm font-medium">Ordrar i faktura ({editInvoice.items.length} st)</p>
                      {editInvoice.items.map((item) => (
                        <div key={item.id} className="flex items-center justify-between gap-2 rounded-lg border p-3">
                          <div className="text-sm">
                            <span className="font-medium">Order {item.order?.orderNumber || item.orderId.slice(0, 8)}</span>
                            {item.order?.orderDate && (
                              <span className="text-muted-foreground ml-2">
                                {new Date(item.order.orderDate).toLocaleDateString("sv-SE")}
                              </span>
                            )}
                            <span className="ml-2">{item.amount} kr</span>
                          </div>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-destructive"
                            onClick={() => {
                              deleteItemMutation.mutate(
                                { invoiceId: editInvoice.id, itemId: item.id },
                                {
                                  onSuccess: (data: any) => {
                                    if (data?.invoice) {
                                      setEditInvoice(data.invoice);
                                    } else {
                                      const updated = {
                                        ...editInvoice,
                                        items: editInvoice.items.filter(i => i.id !== item.id),
                                        totalAmount: (
                                          editInvoice.items
                                            .filter(i => i.id !== item.id)
                                            .reduce((sum, i) => sum + parseFloat(i.amount || "0"), 0)
                                        ).toFixed(2),
                                      };
                                      setEditInvoice(updated);
                                    }
                                  },
                                }
                              );
                            }}
                            disabled={deleteItemMutation.isPending}
                            data-testid={`button-remove-invoice-item-${item.id}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <div className="border-t pt-2 flex justify-between items-center">
                        <span className="text-sm font-medium">Totalt</span>
                        <span className="font-bold">{editInvoice.totalAmount} kr</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter className="px-4 py-3 border-t">
                <Button variant="outline" onClick={() => setEditInvoice(null)}>
                  Avbryt
                </Button>
                <Button
                  onClick={handleSaveEdit}
                  disabled={updateInvoiceMutation.isPending}
                  data-testid="button-save-invoice-edit"
                >
                  {updateInvoiceMutation.isPending ? "Sparar..." : "Spara"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deletingInvoiceId} onOpenChange={(open) => !open && setDeletingInvoiceId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Radera faktura?</AlertDialogTitle>
            <AlertDialogDescription>
              Detta kommer att permanent radera fakturan och frigöra alla kopplade ordrar så de kan faktureras igen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingInvoiceId && deleteMutation.mutate(deletingInvoiceId)}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground"
            >
              {deleteMutation.isPending ? "Raderar..." : "Radera"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
